import { Button, Tooltip } from "@chakra-ui/react";
import { BulkAddPortTx } from "features/commitments/BulkAddPortTx";
import { useRefresh } from "features/fees/dashboard-pages/FeeBasisTab/useRefresh";
import {
  FeePostUseBreakdownBasisAdjustmentsTable,
  makeEmptyBasisAdjustmentsRow,
} from "features/fees/post/use-breakdown/FeePostUseBreakdownBasisAdjustmentsTable";
import {
  useGetDraftPortfolioEventSuspense,
  usePortfolioTransactionListSuspense,
  usePortfolioTransactionReadOnlyTableRetriveSuspense,
} from "gen/endpoints/portfolio-transactions/portfolio-transactions";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { PortfolioTransactionBulkCreateDataRequest } from "gen/model/portfolioTransactionBulkCreateDataRequest";
import { PortfolioTransactionLinkMetadata } from "gen/model/portfolioTransactionLinkMetadata";
import React, { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { QueryBoundary } from "shared/api/QueryBoundary";
import { Restricted } from "shared/components/access/Restricted";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import { ViewEditDropdownButton } from "shared/components/button/ViewEditDropdown";
import Form from "shared/components/form/Form";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { GetLinkInfoForCellFn } from "shared/components/table/data/DataTableTypes";
import { useMaybernTableFormRowController } from "shared/components/table/maybern/MaybernTableFormRowController";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { InfoCircleIcon } from "shared/icons/InfoCircleIcon";
import { rootRoute } from "shared/navigation/Routes";
import { useBoolean } from "usehooks-ts";

export const FeeBasisTable: React.FC<{
  editModeEnabled: boolean;
  setEditModeDisabled: () => void;
  setEditMode: (value: boolean) => void;
  feeChargeEventSelected: string;
}> = ({
  editModeEnabled,
  setEditModeDisabled,
  setEditMode,
  feeChargeEventSelected,
}) => {
  const { t } = useTranslation(["fees", "common"]);
  const { fundFamilyId } = useRequiredStringParams();
  const {
    isRefreshLoading,
    getErrorForCell,
    onSubmitDraft,
    mutateFundFamilyEventState,
  } = useRefresh({ fundFamilyId, showToasts: true, feeChargeEventSelected });

  const { data: portfolioTransactionData } =
    usePortfolioTransactionReadOnlyTableRetriveSuspense(fundFamilyId);
  const { data: portfolioTransactionListData } =
    usePortfolioTransactionListSuspense(fundFamilyId, {
      include_only_drafts: true,
    });

  const form = useForm<PortfolioTransactionBulkCreateDataRequest>({
    defaultValues: {
      portfolio_transactions: portfolioTransactionListData.length
        ? portfolioTransactionListData
        : [makeEmptyBasisAdjustmentsRow()],
    },
  });

  useEffect(() => {
    form.reset({
      portfolio_transactions: portfolioTransactionListData.length
        ? portfolioTransactionListData
        : [makeEmptyBasisAdjustmentsRow()],
    });
  }, [form, portfolioTransactionListData]);

  const { isDirty } = form.formState;

  const { propsForRows } = useMaybernTableFormRowController(
    form,
    "portfolio_transactions",
  );

  const portfolioTxDraftEvent = useGetDraftPortfolioEventSuspense(fundFamilyId);
  const draftFundFamilyEventId = portfolioTxDraftEvent.data.draft_tx?.id;

  const onSubmitFinalize = async () => {
    if (!draftFundFamilyEventId) return;
    await mutateFundFamilyEventState({
      fundFamilyId,
      fundFamilyEventId: draftFundFamilyEventId,
    });
    form.reset({
      portfolio_transactions: [makeEmptyBasisAdjustmentsRow()],
    });
    setEditModeDisabled();
  };

  const navigate = useNavigate();

  const viewAllocations = useCallback(() => {
    navigate(
      rootRoute({})
        .fundFamilies({})
        .fundFamily({
          fundFamilyId,
        })
        .fees({})
        .draftAllocations({}).$,
    );
  }, [fundFamilyId, navigate]);

  const getViewAllocationButton = () => {
    if (!editModeEnabled || !draftFundFamilyEventId) return null;
    const button = (
      <Button
        variant="link"
        size="xs"
        onClick={viewAllocations}
        isDisabled={isDirty || isRefreshLoading}
      >
        {t("fees:feeUseBreakdown.allocations.nav")}
      </Button>
    );
    if (isDirty || isRefreshLoading) {
      return (
        <Tooltip
          label={t(
            "fees:feeUseBreakdown.basisAdjustmentsTable.viewAllocationToolTip",
          )}
        >
          {button}
        </Tooltip>
      );
    }
    return button;
  };

  const getLinkInfoForCell = useCallback<GetLinkInfoForCellFn>(
    (props) => {
      if (props.type !== "data") return undefined;
      const { cell, columnContext } = props;
      if (columnContext.columnId === "fund_family_event_id" && cell.value) {
        return {
          type: "generic",
          colorScheme: "tertiary",
          to: rootRoute({})
            .fundFamilies({})
            .fundFamily({
              fundFamilyId,
            })
            .fees({})
            .historicalAllocations({
              fundFamilyEventId: (
                cell.metadata as PortfolioTransactionLinkMetadata
              ).fund_family_event_id,
            }).$,
        };
      }
    },
    [fundFamilyId],
  );
  const {
    value: isBulkAddModalOpen,
    setTrue: openBulkAddModal,
    setFalse: closeBulkAddModal,
  } = useBoolean();
  return (
    <>
      <BulkAddPortTx
        isOpen={isBulkAddModalOpen}
        onClose={closeBulkAddModal}
        fundFamilyId={fundFamilyId}
        feeChargeEventSelected={feeChargeEventSelected}
      />
      <Form
        form={form}
        onSubmit={onSubmitFinalize}
        onSaveProgress={() => onSubmitDraft(propsForRows.rowData!)}
        onError={() => true}
        isDisabled={isRefreshLoading}
      >
        <TableBox>
          <TableBoxHeader>
            <TableBoxTitle>
              {t("fees:feeUseBreakdown.basisAdjustmentsTable.title")}
            </TableBoxTitle>
            <DataTableBoxActions>
              {editModeEnabled && (
                <Button
                  isDisabled={isRefreshLoading}
                  onClick={openBulkAddModal}
                  variant="link"
                >
                  {t("fees:feeUseBreakdown.bulkAdd.button")}
                </Button>
              )}
              {getViewAllocationButton()}
              <ViewEditDropdownButton
                isEditing={editModeEnabled}
                setIsEditing={setEditMode}
              />
              {editModeEnabled && (
                <>
                  {isDirty && (
                    <Restricted permission={PermissionTypeEnum.write}>
                      <Button
                        size="xs"
                        colorScheme="tertiary"
                        onClick={() => onSubmitDraft(propsForRows.rowData!)}
                        isDisabled={isRefreshLoading}
                      >
                        {t("common:refresh")}
                        <Tooltip
                          key="refreshToolTip"
                          label={t("fees:feeBasisTab.tool_tip")}
                        >
                          <InfoCircleIcon
                            w="12px"
                            h="12px"
                            ml="10px"
                            color="gray.400"
                          />
                        </Tooltip>
                      </Button>
                    </Restricted>
                  )}

                  {!isDirty && draftFundFamilyEventId && (
                    <Restricted permission={PermissionTypeEnum.approve}>
                      <FormSubmitButton isDisabled={isRefreshLoading} size="xs">
                        {t("fees:feeBasisTab.finalize")}
                      </FormSubmitButton>
                    </Restricted>
                  )}
                </>
              )}
            </DataTableBoxActions>
          </TableBoxHeader>
          <TableBoxBody>
            {editModeEnabled ? (
              <QueryBoundary>
                <FeePostUseBreakdownBasisAdjustmentsTable
                  fundFamilyId={fundFamilyId}
                  getErrorForCell={getErrorForCell}
                  form={form}
                />
              </QueryBoundary>
            ) : (
              <DataTable
                getLinkInfoForCell={getLinkInfoForCell}
                data={portfolioTransactionData}
                showAllRows={false}
              />
            )}
          </TableBoxBody>
        </TableBox>
      </Form>
    </>
  );
};
